
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ImagePlus, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AIImageGeneratorProps {
  stationId: string;
  stationData: any;
  existingImageUrl?: string;
  onImageGenerated?: (imageUrl: string) => void;
  className?: string;
}

const AIImageGenerator = ({
  stationId,
  stationData,
  existingImageUrl,
  onImageGenerated,
  className,
}: AIImageGeneratorProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(existingImageUrl);
  const { toast } = useToast();

  const generateImage = async () => {
    try {
      setIsGenerating(true);
      toast({
        title: 'Generating image',
        description: 'This may take up to 30 seconds...',
      });
      
      const { data, error } = await supabase.functions.invoke('generate-station-image', {
        body: { stationId, stationData },
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data.success && data.imageUrl) {
        setGeneratedImageUrl(data.imageUrl);
        
        if (onImageGenerated) {
          onImageGenerated(data.imageUrl);
        }
        
        toast({
          title: 'Image generated successfully',
          description: 'Your AI station image is ready!',
        });
      } else {
        throw new Error('Failed to generate image');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      toast({
        title: 'Error generating image',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      {generatedImageUrl ? (
        <div className="relative group">
          <img 
            src={generatedImageUrl} 
            alt="AI Generated Station" 
            className="w-full h-auto rounded-lg shadow-md object-cover"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
            <Button 
              variant="outline" 
              size="sm"
              onClick={generateImage}
              disabled={isGenerating}
              className="bg-white/90 hover:bg-white text-black"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Regenerate Image
                </>
              )}
            </Button>
          </div>
          <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-md">
            AI Generated
          </div>
        </div>
      ) : (
        <Button
          onClick={generateImage}
          disabled={isGenerating}
          className="w-full h-32 border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center rounded-lg"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin mb-2 text-blue-500" />
              <span>Generating AI Image...</span>
            </>
          ) : (
            <>
              <ImagePlus className="h-6 w-6 mb-2 text-blue-500" />
              <span>Generate AI Image</span>
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default AIImageGenerator;
